import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { pesarSeca } from '../../api/triviais';
import type { FichaDaRepeticao } from '../../api/repeticao';
import { BlocoDaFicha, JanelaDeDescarte, useEquipamentos } from '../../Ficha';
import { numero, paraNumero, plural } from '../../formato';
import { useSessao } from '../../sessao';
import {
  Aviso, CampoNumerico, ErroDaApi, EscolhaPorCodigo, Selo, TabelaDeLancamento, comoCodigo,
} from '../../ui';
import type { ColunaDeLancamento, LinhaDeLancamento } from '../../ui';
import { MolduraDaRepeticao, useRepeticao } from './Repeticao';
import type { Comparacao, UseRepeticao } from './Repeticao';
import { umidadeNaTela, useTaras } from './RepeticaoLl';

/**
 * A repetição das fichas que comparam só números (onda 323, D7): umidade,
 * LP e massa específica dos sólidos.
 *
 * Sem gráfico: à esquerda, as linhas da repetição e a linha nova, com as
 * colunas da ficha; à direita, as linhas iniciais em leitura. No topo, a
 * comparação do resultado — o «Atual» é SEMPRE o do servidor (a média que o
 * banco tirou das linhas da repetição); a tela só formata, nas casas da
 * norma, e a diferença sai dos dois números já expressos.
 *
 * `RepeticaoNumerica` é a peça comum (a moldura, a comparação e as duas
 * tabelas); cada ficha diz as colunas, como desenhar uma linha e como é a
 * linha nova. `RepeticaoDeCapsulas` é a de umidade e LP — uma cápsula por
 * linha, como a Pesagem úmida; a da MES mora em `MassaEspecifica.tsx`, ao
 * lado do selo da origem de M₁.
 */

// ══ o resultado ═══════════════════════════════════════════════════════

export interface ResultadoNumerico {
  /** «Teor de umidade», «LP», «ρs». */
  rotulo: string;
  unidade?: string;
  /** As casas da norma (umidade 1, LP 0, ρs 3). */
  casas: number;
  /** O número do servidor, ou `null` enquanto não sai. */
  valor(f: any): number | null;
  /** Um resultado que é palavra, e não número (o LP «NP»). */
  texto?(f: any): string | null;
  /** A linha embaixo do número. */
  nota?(f: any, lado: 'inicial' | 'atual'): ReactNode;
  /** A unidade da diferença («p.p.» na umidade; «pontos» no LP). */
  unidadeDaDiferenca?: string;
  /** Quando ainda não há diferença, por quê. */
  semDiferenca?: string;
}

/** O número nas casas da norma, arredondado como o laudo o mostra. */
const expresso = (v: number, casas: number) => Number(v.toFixed(casas));

/** «+10,0», «−0,012», «0» — a diferença com as casas e o menos tipográfico. */
export function diferencaComSinal(d: number, casas: number): string {
  const z = expresso(d, casas);
  if (z === 0) return numero(0, casas);
  return `${z > 0 ? '+' : '−'}${numero(Math.abs(z), casas)}`;
}

export function comparacaoNumerica(res: ResultadoNumerico, inicial: any, atual: any): Comparacao {
  const lado = (f: any) => {
    const t = res.texto?.(f) ?? null;
    const v = t == null ? res.valor(f) : null;
    return { t, v: v != null ? expresso(v, res.casas) : null };
  };
  const i = lado(inicial);
  const a = lado(atual);
  const valor = (x: { t: string | null; v: number | null }) =>
    x.t ?? (x.v != null ? numero(x.v, res.casas) : '—');
  const dif = i.v != null && a.v != null ? a.v - i.v : null;
  return {
    inicial: { valor: valor(i), unidade: i.v != null ? res.unidade : undefined,
      nota: res.nota?.(inicial, 'inicial') },
    atual: { valor: valor(a), unidade: a.v != null ? res.unidade : undefined,
      nota: res.nota?.(atual, 'atual') },
    diferenca: {
      valor: dif != null ? diferencaComSinal(dif, res.casas) : '—',
      unidade: dif != null ? res.unidadeDaDiferenca ?? res.unidade : undefined,
      nota: dif == null ? res.semDiferenca ?? 'sai com o resultado da repetição'
        : expresso(dif, res.casas) === 0 ? `o mesmo ${res.rotulo}`
        : `${res.rotulo} atual ${dif < 0 ? 'menor' : 'maior'} que o inicial`,
    },
  };
}

// ══ a peça comum ══════════════════════════════════════════════════════

export interface RepeticaoNumericaProps {
  id: string;
  ficha: FichaDaRepeticao;
  /** O nome do ensaio, no rótulo da janela de adotar. */
  titulo: string;
  podeInterpretar: boolean;
  congelado: boolean;
  aoMudar(): void | Promise<void>;
  /** O estado, quando a ficha precisa dele fora (a mesma `useRepeticao`). */
  repeticao?: UseRepeticao;
  resultado: ResultadoNumerico;
  /** As linhas de uma leitura da ficha (`determinacoes`). */
  linhas(f: any): any[];
  /** As colunas da ficha, na ordem da bancada. */
  colunas: ColunaDeLancamento[];
  /** Uma linha gravada, nas colunas da ficha (inicial e repetição). */
  linha(l: any, f: any): LinhaDeLancamento;
  /** A última célula das linhas da repetição: «Descartar», a seca. */
  acao?(l: any, r: UseRepeticao): ReactNode;
  /** A linha nova — `null` quando não se lança (sem permissão, NP). */
  nova?(r: UseRepeticao): LinhaDeLancamento | null;
  /** O que vem sob a tabela da repetição: a dica, o erro, as janelas. */
  rodape?(r: UseRepeticao): ReactNode;
  /** Avisos acima das tabelas (o NP do LP). */
  avisos?(r: UseRepeticao): ReactNode;
  /** As linhas iniciais lado a lado (umidade, LP) ou embaixo (MES). */
  inicialEmbaixo?: boolean;
  /** «Determinações novas · tentativa N». */
  rotuloDasLinhas?: string;
  /** A Interpretação de sempre, sem repetição aberta. */
  children?: ReactNode;
}

export function RepeticaoNumerica(props: RepeticaoNumericaProps) {
  const {
    id, ficha, titulo, podeInterpretar, congelado, aoMudar, resultado, linhas, colunas,
    linha, acao, nova, rodape, avisos, inicialEmbaixo, children,
  } = props;
  const rotuloDasLinhas = props.rotuloDasLinhas ?? 'Determinações';
  const ordenadas = (f: any) => [...(linhas(f) ?? [])].sort((a, b) => a.ordem - b.ordem);

  const lancamento = (r: UseRepeticao) => {
    const atual = r.dados?.atual;
    const n = nova?.(r) ?? null;
    const gravadas = ordenadas(atual).map((l) => {
      const x = linha(l, atual);
      return acao ? { ...x, celulas: [...x.celulas, acao(l, r)] } : x;
    });
    return (
      <BlocoDaFicha titulo={`${rotuloDasLinhas} novas · tentativa ${r.dados?.aberta?.numero ?? ''}`}
        sub={n ? 'Lance aqui mesmo: cada linha vai para a repetição, pela rota de sempre da ficha.'
          : 'As linhas da repetição, à medida que são lançadas.'}>
        <div className="fe-tabela">
          <TabelaDeLancamento rotulo={`${rotuloDasLinhas} da repetição — lançadas e a nova`}
            classe="capsulas fe-lanc repeticao-lanc"
            colunas={acao || n ? [...colunas, { texto: '' }] : colunas}
            linhas={n ? [...gravadas, n] : gravadas} />
          {rodape?.(r)}
        </div>
      </BlocoDaFicha>
    );
  };

  const inicial = (r: UseRepeticao) => {
    const ini = r.dados?.inicial;
    if (!ini) return null;
    const t = resultado.texto?.(ini) ?? null;
    const v = resultado.valor(ini);
    const n = ordenadas(ini).filter((l) => !(l.descartada || l.descartado)).length;
    return (
      <BlocoDaFicha titulo={`${rotuloDasLinhas} iniciais · tentativa ${r.dados?.vigente ?? ''}`}
        selo={<Selo tom="neutro">{resultado.rotulo} {t ?? (v != null
          ? `${numero(v, resultado.casas)}${resultado.unidade ? ` ${resultado.unidade}` : ''}` : '—')}
          {' · '}{plural(n, 'válida', 'válidas')}</Selo>}
        sub="Em leitura durante a repetição. Se você adotar a nova, saem da conta e do laudo e ficam no histórico.">
        <div className="fe-tabela">
          <LinhasEmLeitura linhas={ordenadas(ini)} f={ini} colunas={colunas} linha={linha}
            rotulo={`${rotuloDasLinhas} iniciais`} />
        </div>
      </BlocoDaFicha>
    );
  };

  return (
    <MolduraDaRepeticao ensaioId={id} ficha={ficha} titulo={titulo}
      podeInterpretar={podeInterpretar} congelado={congelado} repeticao={props.repeticao}
      aoMudar={aoMudar}
      frase={<>As linhas novas entram na comparação assim que o servidor tira o resultado
        delas. O laudo continua com o resultado inicial até você adotar a repetição.</>}
      comparacao={(i, a) => comparacaoNumerica(resultado, i, a)}
      lancamento={(r) => <>{avisos?.(r)}{lancamento(r)}</>}
      grafico={inicialEmbaixo ? undefined : inicial}
      depois={inicialEmbaixo ? inicial : undefined}>
      {children}
    </MolduraDaRepeticao>
  );
}

/** As linhas de uma tentativa em leitura (a inicial, as anteriores). */
export function LinhasEmLeitura({ linhas, f, colunas, linha, rotulo }: {
  linhas: any[]; f: any; colunas: ColunaDeLancamento[];
  linha(l: any, f: any): LinhaDeLancamento; rotulo: string;
}) {
  return (
    <TabelaDeLancamento rotulo={rotulo} classe="capsulas fe-lanc repeticao-leitura"
      colunas={colunas}
      linhas={[...linhas].sort((a, b) => a.ordem - b.ordem).map((l) => linha(l, f))} />
  );
}

/**
 * A Interpretação de sempre das fichas numéricas: o resultado que vai ao
 * laudo e as linhas de que ele saiu, em leitura (sem repetição aberta).
 */
export function ResultadoVigente({ resultado, f, linhas, colunas, linha, rotulo }: {
  resultado: ResultadoNumerico; f: any; linhas: any[]; colunas: ColunaDeLancamento[];
  linha(l: any, f: any): LinhaDeLancamento; rotulo: string;
}) {
  const t = resultado.texto?.(f) ?? null;
  const v = resultado.valor(f);
  return (
    <BlocoDaFicha titulo={`${resultado.rotulo} · o resultado do laudo`}
      selo={<Selo tom="neutro">{t ?? (v != null
        ? `${numero(v, resultado.casas)}${resultado.unidade ? ` ${resultado.unidade}` : ''}`
        : 'ainda não sai')}</Selo>}
      sub="É este o número que vai ao laudo. Para conferir com uma tentativa nova, repita o ensaio aqui: o laudo só muda se você adotar a repetição.">
      {linhas.length > 0 && (
        <div className="fe-tabela">
          <LinhasEmLeitura linhas={linhas} f={f} colunas={colunas} linha={linha} rotulo={rotulo} />
        </div>
      )}
    </BlocoDaFicha>
  );
}

// ══ umidade e LP: uma cápsula por linha ═══════════════════════════════

const CAT_CAPSULA = 'capsula';
const vivo = (l: any) => !(l.descartada || l.descartado);
const naEstufa = (l: any) => vivo(l) && l.massa_capsula_solo_seco == null;

/** As colunas da Pesagem úmida e da Pesagem seca, na ordem da bancada (D9). */
export const COLUNAS_DE_CAPSULA: ColunaDeLancamento[] = [
  { texto: 'Cápsula' },
  { texto: 'Úmida + cáp. (g)', num: true },
  { texto: 'Seca + cáp. (g)', num: true },
  { texto: 'w (%)', num: true },
];

/** Uma cápsula gravada, em leitura; `fora` marca a depurada dos 5 % (LP). */
export function linhaDeCapsula(l: any, fora?: boolean): LinhaDeLancamento {
  const morta = !vivo(l);
  return {
    chave: l.id,
    classe: morta ? 'descartada' : naEstufa(l) ? 'nova' : undefined,
    titulo: <>Determinação {l.ordem} · <span className="cod">{l.capsula}</span></>,
    resultado: l.umidade != null && !morta
      ? <span className="n">{numero(l.umidade, 2)} %</span> : undefined,
    nota: morta ? `Descartada: ${l.motivo_descarte ?? ''}` : undefined,
    semCartao: l.umidade != null ? [0, 3] : [0],
    celulas: [
      <span className="cod">{l.capsula}</span>,
      numero(l.massa_capsula_solo_umido, 2),
      l.massa_capsula_solo_seco != null ? numero(l.massa_capsula_solo_seco, 2) : '—',
      morta ? 'descartada'
        : l.umidade == null ? <span className="sub">espera a seca</span>
        : <>{numero(l.umidade, 2)}{fora ? <> <Selo tom="atencao">fora dos 5 %</Selo></> : null}</>,
    ],
  };
}

interface Rascunho { cap: string; umido: string; seco: string }
const vazio = (): Rascunho => ({ cap: '', umido: '', seco: '' });

export function RepeticaoDeCapsulas({
  id, ficha, titulo, podeInterpretar, congelado, aoMudar, resultado, dica,
  gravar, descartar, foraDaMedia, avisos, semLancar, children,
}: {
  id: string;
  ficha: 'umidade' | 'limite-plasticidade';
  titulo: string;
  podeInterpretar: boolean;
  congelado: boolean;
  aoMudar(): void | Promise<void>;
  resultado: ResultadoNumerico;
  /** A regra da norma sob a tabela («NBR 6457: mínimo de 3 determinações»). */
  dica: string;
  /** A rota de sempre da ficha (POST determinações). */
  gravar(corpo: unknown): Promise<unknown>;
  descartar(linhaId: string, motivo: string): Promise<unknown>;
  /** A depurada dos 5 % (LP), pela lista que o servidor manda. */
  foraDaMedia?(l: any, f: any): boolean;
  avisos?(r: UseRepeticao): ReactNode;
  /** Por que não se lança (o NP do LP): some a linha nova. */
  semLancar?(r: UseRepeticao): boolean;
  children?: ReactNode;
}) {
  const equip = useEquipamentos();
  /* R1: com a repetição aberta, quem interpreta também lança nela. */
  const { pode } = useSessao();
  const lanca = pode('ensaio.lancar') || pode('ensaio.interpretar');
  const rep = useRepeticao(id, ficha, aoMudar);
  const [rascunho, setRascunho] = useState<Rascunho>(vazio);
  const [secas, setSecas] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [descartando, setDescartando] = useState<any | null>(null);

  const conhecidas = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of [rep.dados?.inicial, rep.dados?.atual]) {
      for (const l of (f?.determinacoes ?? []) as any[]) {
        if (l.tara != null && !m.has(l.capsula_equipamento_id)) {
          m.set(l.capsula_equipamento_id, Number(l.tara));
        }
      }
    }
    return m;
  }, [rep.dados]);
  const opcoes = useMemo(() => equip.filter((e) => e.categoria === CAT_CAPSULA)
    .map((e) => comoCodigo({ valor: e.id, texto: e.codigo_patrimonial, detalhe: e.nome })),
  [equip]);
  const tara = useTaras(conhecidas, rascunho.cap);
  const mexer = (c: keyof Rascunho, v: string) => setRascunho((r) => ({ ...r, [c]: v }));

  const umido = paraNumero(rascunho.umido);
  const seco = paraNumero(rascunho.seco);
  const wDigitado = umidadeNaTela(umido, seco, tara);
  const falta = !rascunho.cap ? 'Falta escolher a cápsula.'
    : umido == null ? 'Falta a massa de cápsula + solo úmido.' : null;

  /* O que acabou de gravar sai do rascunho LOGO (`limpar`), antes das
     releituras: senão o que já se digita na linha seguinte se perderia. */
  const executar = async (
    marca: string, chamada: () => Promise<unknown>, r: UseRepeticao, limpar?: () => void,
  ) => {
    setSalvando(marca); setErro(null);
    /* A ficha relê também: os resumos das etapas de bancada contam as linhas
       da repetição (R2) e precisam ver a que acabou de entrar. */
    try {
      await chamada();
      limpar?.();
      /* Gravado: o botão se solta JÁ — a releitura não segura o segundo
         clique (que antes caía calado em `salvando`). */
      setSalvando(null);
      await r.recarregar();
      void Promise.resolve(aoMudar()).catch(() => undefined);
      return true;
    }
    catch (e) { setErro(e); return false; }
    finally { setSalvando(null); }
  };
  const estufaMudou = () => window.dispatchEvent(new CustomEvent('labmanager:estufa-mudou'));
  /* O cabeçalho não muda por aqui: vai a balança gravada, a mesma (R1). */
  const balancaDe = (r: UseRepeticao) => (r.dados?.atual?.balanca ?? r.dados?.inicial?.balanca)?.id;

  const lancar = async (r: UseRepeticao) => {
    if (falta || salvando) return;
    const linhas = (r.dados?.atual?.determinacoes ?? []) as any[];
    const ordem = linhas.reduce((m, l) => Math.max(m, Number(l.ordem)), 0) + 1;
    const balanca = balancaDe(r);
    const ok = await executar('lancar', () => gravar({
      ...(balanca ? { balancaEquipamentoId: balanca } : {}),
      determinacoes: [{
        ordem,
        capsulaEquipamentoId: rascunho.cap,
        massaCapsulaSoloUmido: umido,
        ...(seco != null ? { massaCapsulaSoloSeco: seco } : {}),
      }],
    }), r, () => setRascunho(vazio()));
    if (ok && seco == null) estufaMudou();
  };

  const pesar = async (l: any, r: UseRepeticao) => {
    const massa = paraNumero(secas[l.id] ?? '');
    if (massa == null || salvando) return;
    const balanca = balancaDe(r);
    const ok = await executar(`seca-${l.id}`, () => pesarSeca({
      linhaId: l.id, massaCapsulaSoloSeco: massa,
      ...(balanca ? { balancaEquipamentoId: balanca } : {}),
    }), r);
    if (ok) {
      setSecas((s) => { const n = { ...s }; delete n[l.id]; return n; });
      estufaMudou();
    }
  };

  const linha = (l: any, f: any): LinhaDeLancamento => {
    const x = linhaDeCapsula(l, foraDaMedia?.(l, f));
    if (!(lanca && naEstufa(l) && f === rep.dados?.atual)) return x;
    /* A seca da repetição se digita ali mesmo (ou entra pela Estufa). */
    const celulas = [...x.celulas];
    celulas[2] = (
      <span className="repeticao-seca">
        <CampoNumerico rotulo={`Seca + cápsula — ${l.capsula}`} rotuloOculto
          placeholder="da estufa" value={secas[l.id] ?? ''}
          disabled={salvando !== null}
          onChange={(e) => setSecas((s) => ({ ...s, [l.id]: e.target.value }))} />
        <button type="button" className="botao secundario pequeno" data-gravar-linha=""
          aria-disabled={!secas[l.id] || salvando !== null}
          onClick={() => void pesar(l, rep)}>
          {salvando === `seca-${l.id}` ? 'Gravando…' : 'Gravar'}
        </button>
      </span>
    );
    return { ...x, celulas };
  };

  const acao = (l: any) => (!vivo(l) || !lanca ? null : (
    <button type="button" className="botao discreto pequeno"
      aria-label={`Descartar a determinação ${l.ordem} (${l.capsula})`}
      disabled={salvando !== null} onClick={() => setDescartando(l)}>
      Descartar
    </button>
  ));

  const nova = (r: UseRepeticao): LinhaDeLancamento | null => {
    if (!lanca || semLancar?.(r)) return null;
    const ordem = ((r.dados?.atual?.determinacoes ?? []) as any[])
      .reduce((m, l) => Math.max(m, Number(l.ordem)), 0) + 1;
    return {
      chave: 'nova',
      classe: 'nova',
      titulo: `Determinação ${ordem} · nova`,
      resultado: wDigitado != null
        ? <span className="n repeticao-digitando">{numero(wDigitado, 2)} %</span> : undefined,
      semCartao: [3],
      celulas: [
        <EscolhaPorCodigo rotulo="Cápsula da determinação nova" rotuloOculto opcoes={opcoes}
          valor={rascunho.cap} aoEscolher={(v) => mexer('cap', v)} />,
        <CampoNumerico rotulo="Úmida + cápsula da determinação nova (g)" rotuloOculto
          value={rascunho.umido} onChange={(e) => mexer('umido', e.target.value)} />,
        <CampoNumerico rotulo="Seca + cápsula da determinação nova (g)" rotuloOculto
          value={rascunho.seco} placeholder="ou depois"
          onChange={(e) => mexer('seco', e.target.value)} />,
        <b className="repeticao-digitando">{wDigitado != null ? numero(wDigitado, 2) : ''}</b>,
        <button type="button" className="botao secundario pequeno fe-gravar-linha" data-gravar-linha=""
          aria-disabled={falta !== null || salvando !== null}
          title={falta ?? 'Grava a determinação na repetição (Enter)'}
          onClick={() => void lancar(r)}>
          {salvando === 'lancar' ? 'Gravando…' : 'Gravar determinação'}
        </button>,
      ],
    };
  };

  const codigo = opcoes.find((o) => o.valor === rascunho.cap)?.codigo;
  const rodape = (r: UseRepeticao) => (
    <>
      {lanca && !semLancar?.(r)
        ? (
          <p className="sub repeticao-dica">
            {codigo && <>Tara da {codigo}: {tara != null ? `${numero(tara, 2)} g` : 'sem calibração interna'} · </>}
            {dica}. Sem a seca, a cápsula espera a estufa (a seca também entra pela Bancada › Estufa).
          </p>
        )
        : !lanca && (
          <p className="sub repeticao-dica">
            Lançar pede a permissão de lançar dados de ensaio, que o seu perfil não tem: as
            determinações chegam aqui quando o laboratório as lança na Pesagem úmida.
          </p>
        )}
      <ErroDaApi erro={erro} />
      <JanelaDeDescarte
        alvo={descartando ? `a determinação ${descartando.ordem} · ${descartando.capsula ?? ''}` : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={async (motivo) => {
          const alvo = descartando;
          const ok = await executar('descarte', () => descartar(alvo.id, motivo), r);
          if (ok && naEstufa(alvo)) estufaMudou();
          return ok;
        }}
        salvando={salvando !== null} />
    </>
  );

  return (
    <RepeticaoNumerica id={id} ficha={ficha} titulo={titulo} podeInterpretar={podeInterpretar}
      congelado={congelado} aoMudar={aoMudar} repeticao={rep} resultado={resultado}
      linhas={(f) => f?.determinacoes ?? []} colunas={COLUNAS_DE_CAPSULA}
      linha={linha} acao={acao} nova={nova} rodape={rodape} avisos={avisos}>
      {children}
    </RepeticaoNumerica>
  );
}

/** O aviso do NP na repetição do LP: declarado NP, não há o que lançar. */
export function AvisoDoNp() {
  return (
    <Aviso tipo="info" titulo="Declarado NP">
      O cilindro de 3 mm não se formou, e NP não convive com determinação lançada
      (RN-162). Para repetir com determinações, quem lança retira a declaração de NP
      nas Condições; até lá não há linha nova aqui.
    </Aviso>
  );
}
